// Canonical JSON + checksum, shared shape with P1's Python
// `autopilot_binding_common.canonical_json` (fixture-verified — see test 34,
// skipped in this run pending P1).
//
// Canonicalization rule: object keys sorted by UTF-16 code UNIT via plain
// `Array.prototype.sort()` — this matches JS string default ordering and, for
// ASCII-ish key names (the only kind binding/journal objects use), matches
// Python's default string sort (which is by Unicode code POINT). Code unit
// vs code point only diverges for keys containing astral-plane characters
// (surrogate pairs), which never occurs in our key names — documented here
// instead of doing the more expensive codePointAt-based comparator.
//
// No whitespace: build a recursively key-sorted plain structure, then
// `JSON.stringify(structure)` with no space args. Numbers go through native
// `JSON.stringify` — no hand-rolled number formatting. Arrays keep element
// order; only object keys are sorted.
import { createHash } from "node:crypto";

type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

function sortKeysDeep(value: JsonValue): JsonValue {
  if (Array.isArray(value)) {
    return value.map(sortKeysDeep);
  }
  if (value !== null && typeof value === "object") {
    const sortedKeys = Object.keys(value).sort();
    const out: { [key: string]: JsonValue } = {};
    for (const key of sortedKeys) {
      out[key] = sortKeysDeep((value as { [key: string]: JsonValue })[key] as JsonValue);
    }
    return out;
  }
  return value;
}

export function canonicalJson(value: unknown): string {
  const sorted = sortKeysDeep(value as JsonValue);
  return JSON.stringify(sorted);
}

export function sha256Checksum(canonicalString: string): string {
  return createHash("sha256").update(canonicalString, "utf8").digest("hex");
}

// D08 (dogfood vòng 4, Bug D fix): `result.schema.json`'s `input_hash` is
// the CONTROLLER's field — it identifies "which exact dispatch" produced a
// result, not something the worker can attest to safely (no command adapter
// grants a hash tool; requiring self-attestation only ever produced a
// fabricated value or a refusal in real-worker dogfood). The controller
// already holds every input listed below BEFORE the worker ever sees the
// prompt, so it can compute this deterministically at dispatch time.
export interface ControllerInputHashFields {
  run_id: string;
  ticket_id: string;
  ticket_revision: number;
  prompt_sha256: string;
}

export function computeControllerInputHash(fields: ControllerInputHashFields): string {
  return sha256Checksum(canonicalJson(fields));
}

// Review 260909-p6-m2 INFO: `input_hash` was computed twice with the same
// three-line recipe (sha256Checksum(canonicalJson(prompt)) then
// computeControllerInputHash) in supervisor-loop.ts and
// supervisor-attempt-cycle.ts — one shared helper now, both call it.
export interface AttemptInputHashFields {
  run_id: string;
  ticket_id: string;
  ticket_revision: number;
  prompt: string;
}

/** Hashes `prompt` then delegates to computeControllerInputHash — the exact recipe both call sites need (D08). */
export function computeAttemptInputHash(fields: AttemptInputHashFields): string {
  const promptSha256 = sha256Checksum(canonicalJson(fields.prompt));
  return computeControllerInputHash({
    run_id: fields.run_id,
    ticket_id: fields.ticket_id,
    ticket_revision: fields.ticket_revision,
    prompt_sha256: promptSha256,
  });
}
